import { memo } from 'react';
import { IconButton, Typography, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import {
  ConversationTurn,
  formatTurnTimestamp,
} from '../../utils/group-messages';
import { MarkdownMessage } from './MarkdownMessage';

export interface ConversationTurnGroupProps {
  turn: ConversationTurn;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isLatest: boolean;
  messageBubbleClass: string;
  userMessageClass: string;
  assistantMessageClass: string;
  systemMessageClass: string;
}

const useStyles = makeStyles(theme => ({
  turnGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  turnSeparator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(2, 0),
    '&::before, &::after': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: theme.palette.divider,
    },
  },
  turnTimestamp: {
    fontSize: '0.65rem',
    color: theme.palette.text.hint,
    whiteSpace: 'nowrap',
  },
  collapsedTurn: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 12,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  collapsedPreview: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  collapseIcon: {
    padding: 2,
    color: theme.palette.text.hint,
  },
}));

function getPreviewText(turn: ConversationTurn): string {
  if (turn.userMessage) {
    const text = turn.userMessage.content.trim();
    return text.length > 80 ? `${text.slice(0, 80)}...` : text;
  }
  if (turn.assistantMessages.length > 0) {
    const text = turn.assistantMessages[0].content.trim();
    return text.length > 80 ? `${text.slice(0, 80)}...` : text;
  }
  return '';
}

const ConversationTurnGroupInner = ({
  turn,
  isCollapsed,
  onToggleCollapse,
  isLatest,
  messageBubbleClass,
  userMessageClass,
  assistantMessageClass,
  systemMessageClass,
}: ConversationTurnGroupProps) => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.turnSeparator}>
        <Typography variant="caption" className={classes.turnTimestamp}>
          {formatTurnTimestamp(turn.timestamp)}
        </Typography>
      </div>

      {isCollapsed ? (
        <div
          className={classes.collapsedTurn}
          onClick={onToggleCollapse}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleCollapse();
            }
          }}
          aria-label="Expandir turno da conversa"
        >
          <ChevronRightIcon fontSize="small" className={classes.collapseIcon} />
          <Typography className={classes.collapsedPreview}>
            {getPreviewText(turn)}
          </Typography>
        </div>
      ) : (
        <div className={classes.turnGroup}>
          {!isLatest && (
            <IconButton
              size="small"
              className={classes.collapseIcon}
              onClick={onToggleCollapse}
              aria-label="Colapsar turno"
              style={{ alignSelf: 'flex-start' }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
          {turn.userMessage && (
            <div className={`${messageBubbleClass} ${userMessageClass}`}>
              {turn.userMessage.content}
            </div>
          )}
          {turn.assistantMessages.map(msg =>
            msg.role === 'system' ? (
              <div
                key={msg.id}
                className={`${messageBubbleClass} ${systemMessageClass}`}
              >
                {msg.content}
              </div>
            ) : (
              <div
                key={msg.id}
                className={`${messageBubbleClass} ${assistantMessageClass}`}
              >
                <MarkdownMessage content={msg.content} />
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
};

export const ConversationTurnGroup = memo(ConversationTurnGroupInner);
